import * as React from 'react'
import { Link } from 'react-router-dom'
import { plantHref } from '@atbash-labs/plantview-links'
import {
  usePlantSessionPaint,
  type PlantEdge,
  type PlantPartRef
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { designPixelsToRem } from '../../../lib/ui-density'
import { CollapsibleContent } from '../CollapsibleContent'
import {
  SessionDescendantPaintMarks,
  SessionEntityMark,
  hasSessionPaintMarks,
  useVisibleGhostRels
} from '../session-mark-ui'
import {
  buildBomTree,
  flattenBomTree,
  readBomAssemblyPath,
  readBomOptional,
  readBomQuantity
} from './bom-tree'

export function BomSection({
  equipmentId,
  parts,
  edges,
  truncated
}: {
  equipmentId: string
  parts: PlantPartRef[]
  edges: PlantEdge[]
  truncated: boolean
}): React.ReactElement | null {
  const [expanded, setExpanded] = React.useState(false)
  const autoExpandedRef = React.useRef(false)
  const { active, markFor } = usePlantSessionPaint()
  const partIds = React.useMemo(() => parts.map((part) => part.id), [parts])
  const ghostRels = useVisibleGhostRels(equipmentId)
  const hasDeletedBomRels = React.useMemo(
    () => ghostRels.some((rel) => rel.type === 'HAS_PART' && rel.view === 'deleted'),
    [ghostRels]
  )
  const hasBomPaint = active && (hasSessionPaintMarks(partIds, markFor) || hasDeletedBomRels)

  React.useEffect(() => {
    if (!hasBomPaint || autoExpandedRef.current) return
    autoExpandedRef.current = true
    setExpanded(true)
  }, [hasBomPaint])

  const rows = React.useMemo(
    () => flattenBomTree(buildBomTree(equipmentId, parts, edges)),
    [equipmentId, parts, edges]
  )
  if (rows.length === 0 && !truncated) return null

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          Bill of materials
        </h3>
        <span className="text-xs text-ink-3">{parts.length}</span>
        <span className="ml-auto inline-flex items-center gap-2">
          {active ? (
            <SessionDescendantPaintMarks
              ids={partIds}
              markFor={markFor}
              hasGhosts={hasDeletedBomRels}
            />
          ) : null}
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="m-0 border-0 bg-transparent p-0 text-12 font-medium text-brand hover:underline"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </span>
      </div>
      <CollapsibleContent open={expanded}>
        <div className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
          {truncated && (
            <div className="border-b border-amber-500/30 bg-amber-50 px-3 py-2 text-12 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
              BOM truncated to the first 500 parts (depth ≤ 8). Ancestor parts of every shown row
              are retained.
            </div>
          )}
          {rows.length === 0 ? (
            <div className="px-3 py-2.5 text-12 text-ink-3">
              No parts to display after truncation.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-12">
                <thead>
                  <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
                    <th className="px-3 py-1.5">Part</th>
                    <th className="px-3 py-1.5">Qty</th>
                    <th className="px-3 py-1.5">Assembly path</th>
                    <th className="px-3 py-1.5">P/Ns</th>
                    <th className="px-3 py-1.5">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ node, depth }) => {
                    const quantity = readBomQuantity(node.edge)
                    const assemblyPath = readBomAssemblyPath(node.edge)
                    const optional = readBomOptional(node.edge)
                    const pnBits = [node.part.customer_pn, node.part.manufacturer_pn].filter(
                      (v): v is string => typeof v === 'string' && v.length > 0
                    )
                    const hasFlags =
                      optional ||
                      node.part.is_spare ||
                      node.part.is_critical_spare ||
                      node.part.is_recommended_spare
                    const href = plantHref(node.part.id, 'Part', node.part)
                    const name = (
                      <span className="block min-w-0 truncate font-medium text-ink">
                        {node.part.name}
                      </span>
                    )
                    return (
                      <tr key={`${node.part.id}:${depth}`} className="border-t">
                        <td className="px-3 py-1.5">
                          <div
                            className="flex min-w-0 items-center gap-2"
                            style={{ paddingLeft: designPixelsToRem(depth * 12) }}
                          >
                            <div className="min-w-0 flex-1">
                              {href ? (
                                <Link
                                  to={href}
                                  className="text-ink no-underline hover:text-brand hover:underline"
                                  title={node.part.id}
                                >
                                  {name}
                                </Link>
                              ) : (
                                name
                              )}
                            </div>
                            <SessionEntityMark entityId={node.part.id} />
                          </div>
                        </td>
                        <td className="px-3 py-1.5 font-mono tabular-nums text-ink-2">
                          {quantity !== undefined ? `×${quantity}` : '—'}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-ink-3">{assemblyPath ?? '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-ink-3">
                          {pnBits.length > 0 ? pnBits.join(' · ') : '—'}
                        </td>
                        <td className="px-3 py-1.5">
                          {hasFlags ? (
                            <div className="flex flex-wrap gap-1">
                              {optional && (
                                <Badge variant="secondary" className="px-1.5 py-0 text-10">
                                  optional
                                </Badge>
                              )}
                              {node.part.is_critical_spare && (
                                <Badge variant="destructive" className="px-1.5 py-0 text-10">
                                  critical spare
                                </Badge>
                              )}
                              {node.part.is_recommended_spare && !node.part.is_critical_spare && (
                                <Badge variant="outline" className="px-1.5 py-0 text-10">
                                  recommended spare
                                </Badge>
                              )}
                              {node.part.is_spare &&
                                !node.part.is_critical_spare &&
                                !node.part.is_recommended_spare && (
                                  <Badge variant="outline" className="px-1.5 py-0 text-10">
                                    spare
                                  </Badge>
                                )}
                            </div>
                          ) : (
                            <span className="text-ink-4">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </section>
  )
}
